import React from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowRight } from "lucide-react";

const BRAND = {
  charcoal: "#0b0b0f",
  teal: "#0f766e",
  tealDeep: "#0a4f4a",
  tealLight: "#5eead4",
  terracotta: "#c2603a",
  ochre: "#c99a2e",
  cream: "#f3ead8",
  sandstone: "#d9c7a6",
  ivory: "#faf6ec",
  coral: "#f0836f",
  gold: "#b08d57",
  fuchsia: "#c2337f",
  navy: "#14213d",
  forest: "#1f4d36",
  mint: "#b8e6d3",
};

const MOODS = [
  {
    name: "Earthy Heritage",
    pairs: [BRAND.terracotta, BRAND.ochre, "#9c4a2a"],
    with: "Terracotta, ochre, burnt sienna",
    body: "Woven linen, textile accents and structured leather goods. Resort dressing and upscale daywear, the kind of outfit that suits a cultural event as well as a long lunch.",
  },
  {
    name: "The New Executive",
    pairs: [BRAND.cream, BRAND.sandstone, BRAND.ivory],
    with: "Cream, sandstone, ivory",
    body: "Double-breasted blazers and wide-leg tailored trousers. Workwear with a point of view, for people who take the room seriously without disappearing into it.",
  },
  {
    name: "High Sunset",
    pairs: [BRAND.coral, BRAND.gold, BRAND.fuchsia],
    with: "Coral pink, warm gold, fuchsia",
    body: "Sheer layers, asymmetrical drapes and metallic hardware. The festival and nightlife mood, where the colour gets loud.",
  },
  {
    name: "Monochrome Depth",
    pairs: [BRAND.navy, BRAND.forest, BRAND.mint],
    with: "Deep navy, forest green, mint",
    body: "Silk robes, structured outerwear and fine rib-knits. The most editorial of the four, built for evenings, features and red-carpet moments.",
  },
];

const THREE_WAYS = [
  {
    where: "For work",
    text: "Pair the blazer with cream trousers and keep the accessories minimal.",
    colors: [BRAND.teal, BRAND.cream],
  },
  {
    where: "For a festival",
    text: "Throw it over something sheer and add gold.",
    colors: [BRAND.teal, BRAND.gold],
  },
  {
    where: "For the evening",
    text: "Go tonal with navy and let the colour do the talking.",
    colors: [BRAND.teal, BRAND.navy],
  },
];

function WaterLines() {
  return (
    <svg
      aria-hidden="true"
      viewBox="0 0 800 400"
      preserveAspectRatio="none"
      className="pointer-events-none absolute inset-0 h-full w-full"
    >
      {[0, 1, 2, 3, 4, 5, 6].map((i) => (
        <path
          key={i}
          d={`M -20 ${90 + i * 42} C 160 ${40 + i * 42}, 300 ${
            150 + i * 42
          }, 460 ${90 + i * 42} S 720 ${40 + i * 42}, 820 ${80 + i * 42}`}
          fill="none"
          stroke={BRAND.tealLight}
          strokeWidth="1.25"
          opacity={0.12 + i * 0.03}
        />
      ))}
    </svg>
  );
}

function Swatches({ colors }) {
  return (
    <div className="flex" aria-hidden="true">
      {colors.map((c) => (
        <span
          key={c}
          className="h-3 w-8 first:rounded-l-sm last:rounded-r-sm"
          style={{ backgroundColor: c }}
        />
      ))}
    </div>
  );
}

function H2({ children }) {
  return (
    <h2 className="mt-16 font-serif text-3xl leading-tight tracking-tight text-neutral-950 sm:text-4xl">
      {children}
    </h2>
  );
}

function P({ children, className = "" }) {
  return (
    <p
      className={
        "mt-6 text-[17px] leading-[1.85] text-neutral-700 sm:text-lg " +
        className
      }
    >
      {children}
    </p>
  );
}

export default function TealEditArticle() {
  return (
    <div className="min-h-screen bg-white text-neutral-950">
      <Helmet>
        <title>Africa in Bloom: The Teal Edit | Africa Loud</title>
        <meta
          name="description"
          content="Before the Africa in Bloom campaign opens, here is the colour we're building the season around: teal."
        />
      </Helmet>

      <style>
        {`
          @import url('https://fonts.googleapis.com/css2?family=Fraunces:opsz,wght@9..144,400;700;800&family=Outfit:wght@300;400;600;700&display=swap');

          body, div, p, span, button, a, li {
            font-family: 'Outfit', sans-serif;
          }

          .font-serif, h1, h2, h3, h4 {
            font-family: 'Fraunces', serif !important;
            font-weight: 700;
          }
        `}
      </style>

      <div className="mx-auto max-w-6xl px-4 pt-6 sm:px-6">
        <Link
          to="/"
          className="inline-flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.18em] text-neutral-600 transition hover:text-black"
        >
          <ArrowLeft className="h-3 w-3" />
          Africa Loud
        </Link>
      </div>

      <header
        className="relative mt-6 overflow-hidden"
        style={{ backgroundColor: BRAND.teal }}
      >
        <WaterLines />

        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, ease: "easeOut" }}
          className="relative mx-auto max-w-6xl px-4 py-20 sm:px-6 sm:py-28"
        >
          <p className="text-sm text-white/75">Art x Fashion &middot; Campaign launch</p>

          <h1 className="mt-6 max-w-4xl text-5xl leading-[1.02] tracking-tight text-white sm:text-7xl lg:text-8xl">
            Africa in Bloom: The Teal Edit
          </h1>

          <p className="mt-8 max-w-xl text-lg leading-relaxed text-white/85 sm:text-xl">
            Before the campaign opens, here is the colour we're building the
            season around.
          </p>

          <div className="mt-10 flex" aria-hidden="true">
            {[BRAND.tealDeep, BRAND.teal, BRAND.tealLight].map((c) => (
              <span
                key={c}
                className="h-2 w-12 border border-white/25"
                style={{ backgroundColor: c }}
              />
            ))}
          </div>
        </motion.div>
      </header>

      <main className="mx-auto max-w-2xl px-4 pb-8 pt-14 sm:px-6 sm:pt-20">
        <p className="text-xl leading-[1.7] text-neutral-900 sm:text-2xl">
          Ask most people what "bloom" looks like in African fashion and you'll
          get the same picture: sunshine yellow, warm ochre, bright florals, a
          wardrobe that announces summer from across the street.
        </p>

        <P>
          It's a joyful palette, and it has earned its place. But it's also a
          palette with an expiry date. It belongs to the height of the season,
          and once the season turns, those pieces tend to disappear into the
          back of the cupboard.
        </P>

        <P>
          This year, Africa in Bloom is starting somewhere else. We're starting
          with teal.
        </P>

        <H2>The colour between two worlds</H2>

        <P>
          Teal lives in the space between ocean blue and botanical green. It
          carries the steadiness of deep water and the energy of new growth,
          and that combination is why it feels so current. Trend forecasters
          have been pointing toward this family of blue-greens for the coming
          seasons, and it's easy to see the appeal. People are buying more
          thoughtfully now. They want fewer pieces that do more, and colours
          that don't feel out of date six months later.
        </P>

        <P>
          Teal fits that mood. It has the authority of navy or charcoal, but it
          never reads as safe. Wear it to a meeting and it looks composed. Wear
          it to a festival and it looks intentional. Wear it to dinner and it
          looks like you planned the whole evening around it.
        </P>

        <blockquote
          className="my-14 border-l-4 py-2 pl-6 sm:-ml-8 sm:pl-8"
          style={{ borderColor: BRAND.teal }}
        >
          <p className="font-serif text-2xl leading-snug text-neutral-950 sm:text-3xl">
            For a long time, black, navy and beige were the "neutral" choices.
            Teal is quietly making a case to join them.
          </p>
        </blockquote>

        <H2>It was made for melanin</H2>

        <P>
          There's another reason teal works so well here, and it may be the most
          important one. Some of fashion's favourite "safe" shades, like
          washed-out pastels and muted greys, can flatten deeper skin tones.
          Teal does the opposite. Its rich saturation and balanced undertone
          lift dark and warm skin, adding depth and glow instead of taking it
          away.
        </P>

        <P>
          This matters beyond how it feels in the mirror. It shows up in the
          photographs. Colour that flatters on camera travels further on visual
          platforms, and a teal look tends to stop a scroll. When we say this
          season belongs to teal, we mean that it belongs to the people wearing
          it, too.
        </P>

        <H2>Bloom, without the petals</H2>

        <P>
          Here's where we're pushing back on the obvious. Creative campaigns
          built around "bloom" often lean on the literal: floral motifs,
          familiar prints, soft pastel palettes. Africa in Bloom is not that
          campaign.
        </P>

        <P>
          In this edit, bloom means something bigger. Teal stands for the
          lifeblood of the continent's landscapes: river beds, long coastlines,
          rainforest canopy. It's a deep, grounded colour, the shade of things
          that are alive and growing rather than merely decorative. And it
          makes the classic African companion colours look even better.
          Terracotta, burnt orange, copper and gold all glow against it,
          whether they appear in a woven textile or in a sharp, minimalist cut.
        </P>

        <P>
          That link to textile tradition is part of why we're excited. Colour
          has always carried meaning in African cloth. In Kente, for instance,
          blues and greens are commonly read as symbols of harmony and renewal.
          Teal sits right between them, so it feels less like an imported trend
          and more like a shade that was always waiting for its moment.
        </P>
      </main>

      <section className="mx-auto max-w-6xl px-4 sm:px-6">
        <div className="mx-auto max-w-2xl">
          <h2 className="mt-8 font-serif text-3xl leading-tight tracking-tight text-neutral-950 sm:text-4xl">
            Four ways to wear it
          </h2>
          <P>
            The Teal Edit isn't about one look. It's about range, and across
            the campaign we'll be exploring four moods. Each one pairs teal with
            a different set of companion colours.
          </P>
        </div>

        <div className="mt-10 grid gap-4 sm:grid-cols-2">
          {MOODS.map((m) => (
            <div key={m.name} className="border border-black/15 bg-white p-6">
              <div className="flex items-center justify-between gap-4">
                <h3 className="text-2xl leading-snug text-neutral-950">
                  {m.name}
                </h3>
                <Swatches colors={[BRAND.teal, ...m.pairs]} />
              </div>
              <p className="mt-2 text-sm font-semibold text-neutral-500">
                Teal with {m.with.toLowerCase()}
              </p>
              <p className="mt-4 text-[15px] leading-relaxed text-neutral-700">
                {m.body}
              </p>
            </div>
          ))}
        </div>
      </section>

      <section className="mx-auto max-w-6xl px-4 pt-4 sm:px-6">
        <div className="mx-auto max-w-2xl">
          <h2 className="mt-16 font-serif text-3xl leading-tight tracking-tight text-neutral-950 sm:text-4xl">
            One piece, three ways
          </h2>
          <P>
            The idea underneath all four moods is simple: a single teal piece
            should work harder than you expect. Take a teal blazer.
          </P>
        </div>

        <div className="mt-8 grid gap-4 sm:grid-cols-3">
          {THREE_WAYS.map((w) => (
            <div
              key={w.where}
              className="border border-black/15 p-6"
              style={{ borderTopWidth: 4, borderTopColor: BRAND.teal }}
            >
              <Swatches colors={w.colors} />
              <h3 className="mt-4 text-xl text-neutral-950">{w.where}</h3>
              <p className="mt-2 text-[15px] leading-relaxed text-neutral-700">
                {w.text}
              </p>
            </div>
          ))}
        </div>

        <div className="mx-auto max-w-2xl">
          <P>
            That's the "one piece, three ways" approach, and it will run through
            the campaign's lookbooks, because it shows what teal is really good
            at: keeping up with your life.
          </P>
        </div>
      </section>

      <section className="mx-auto max-w-2xl px-4 pb-6 sm:px-6">
        <H2>What's coming</H2>

        <P>
          This piece is the opening note. Over the coming weeks, Africa in
          Bloom: The Teal Edit will bring you editorial features and styling
          stories, lookbooks built around "one piece, three ways", and an
          interactive digital mini-magazine with shoppable looks. Most
          importantly, it will bring you the people behind the colour:
          designers, stylists, artists and creatives from across the continent
          and the diaspora, showing what teal means in their hands.
        </P>

        <P>
          For now, look at your own wardrobe. Is there something teal in it, or
          something waiting to be paired with it? Start there. The season is
          about to open.
        </P>
      </section>

      <section
        className="mt-16"
        style={{ backgroundColor: BRAND.tealDeep }}
      >
        <div className="mx-auto flex max-w-6xl flex-col gap-6 px-4 py-12 sm:flex-row sm:items-center sm:justify-between sm:px-6">
          <div>
            <h2 className="text-3xl leading-tight text-white sm:text-4xl">
              Follow the campaign
            </h2>
            <p className="mt-2 text-white/75">
              Follow Africa Loud on Instagram, LinkedIn and X as the campaign
              begins.
            </p>
          </div>

          <div className="flex flex-wrap gap-3">
            <a
              href="https://instagram.com/africaloudmag"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center bg-white px-6 py-3 text-[11px] font-semibold uppercase tracking-[0.18em] transition hover:bg-black hover:text-white"
              style={{ color: BRAND.tealDeep }}
            >
              Instagram
              <ArrowRight className="ml-2 h-3 w-3" />
            </a>
            <Link
              to="/collab"
              className="inline-flex items-center border border-white/50 px-6 py-3 text-[11px] font-semibold uppercase tracking-[0.18em] text-white transition hover:bg-white/10"
            >
              Take part
              <ArrowRight className="ml-2 h-3 w-3" />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
